import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { Button } from "@/components/ui/button";
import { User, Users, BookOpen, Mail, Lightbulb, MessageCircle, FileSignature, Heart, Sparkles } from "lucide-react";
import Footer from "@/components/Footer";
// import { ValentinePromoCard } from "@/components/ValentinePromoCard";
import { QuestionSetPromoCard } from "@/components/QuestionSetPromoCard";
import { allQuestionSets } from "@/lib/question-sets";
import { trackEvent } from "@/lib/analytics";

const landingImages = [
  "/assets/landing-1.png",
  "/assets/landing-2.png",
  "/assets/landing-3.png",
  "/assets/landing-4.png",
];

const Landing = () => {
  // Scroll to top when page loads
  useScrollToTop();
  const navigate = useNavigate();

  const [currentImage, setCurrentImage] = useState(() =>
    Math.floor(Math.random() * landingImages.length)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % landingImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // FREEMIUM: Primary CTA — go straight to onboarding (no auth required)
  const handleStartFree = () => {
    sessionStorage.setItem('signup_path', 'freemium');
    trackEvent('Engagement', 'Freemium Started', 'landing_page');
    navigate('/onboarding');
  };

  // Track preview funnel entry and store signup path
  const handlePreviewClick = () => {
    sessionStorage.setItem('signup_path', 'preview');
    trackEvent('Engagement', 'Preview Started', 'landing_page');
    navigate('/preview');
  };

  // Track direct waitlist funnel entry and store signup path
  const handleWaitlistClick = () => {
    sessionStorage.setItem('signup_path', 'direct');
    trackEvent('Engagement', 'Waitlist CTA Clicked', 'landing_page');
    navigate('/waiting');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between px-4 md:px-8 lg:px-16 py-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-3 text-primary">
            <img src="/assets/logo_new_clean.png" alt="Logo" className="h-12 w-12 object-contain" />
            <span className="text-xl font-bold tracking-tight">The Love Contract</span>
          </div>

          <div className="flex gap-2">
            <Link to="/login">
              <Button variant="outline" size="default">
                Login
              </Button>
            </Link>
            <Button size="default" onClick={handleStartFree}>
              Start Free
            </Button>
          </div>
        </div>
      </header>

      <main id="main-content">
        {/* Hero Section */}
        <section className="w-full py-10 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden shadow-2xl">
              {/* Rotating background images */}
              {landingImages.map((src, index) => (
                <img
                  key={src}
                  className={`absolute inset-0 object-cover w-full h-full transition-opacity duration-700 ${
                    index === currentImage ? "opacity-100" : "opacity-0"
                  }`}
                  alt="Couple collaborating on their love contract"
                  src={src}
                />
              ))}
              {/* Dark gradient overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
              {/* Overlaid content */}
              <div className="relative z-10 flex flex-col items-center h-full text-center px-6 pt-8 md:pt-12 lg:pt-16 pb-8 md:pb-12 lg:pb-16">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tighter text-white drop-shadow-lg">
                  Craft Your Love Story, Together.
                </h1>
                <div className="mt-auto flex flex-col items-center">
                  <p className="text-sm md:text-base lg:text-lg font-normal leading-normal text-white/90 max-w-2xl drop-shadow-md">
                    Create a personalized love contract that celebrates your unique partnership. A journey from individual dreams to a shared vision for your future.
                  </p>
                  <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                    <Button size="lg" className="w-full sm:w-auto gap-2" onClick={handleStartFree}>
                      <Sparkles className="h-4 w-4" />
                      Get Started
                    </Button>
                    <Button
                      size="lg"
                      variant="secondary"
                      className="w-full sm:w-auto bg-white/95 text-foreground hover:bg-white"
                      onClick={handlePreviewClick}
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 lg:py-28 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight">
                A Deeper Way to Connect
              </h2>
              <p className="text-lg font-normal leading-normal text-muted-foreground max-w-3xl mx-auto">
                Our unique process guides you and your partner towards deeper understanding and alignment, turning heartfelt conversations into a testament to your commitment.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 text-center items-center">
                <div className="p-3 bg-primary/10 rounded-full mb-2">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-2xl font-bold leading-tight">Reflect Individually</h3>
                  <p className="text-sm text-muted-foreground leading-normal">
                    Explore your hopes and dreams for the future in a private, guided questionnaire.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 text-center items-center">
                <div className="p-3 bg-primary/10 rounded-full mb-2">
                  <Heart className="h-8 w-8 text-primary" />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-2xl font-bold leading-tight">Discover & Share</h3>
                  <p className="text-sm text-muted-foreground leading-normal">
                    Unveil your answers together, celebrating your shared values and discovering new insights.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 text-center items-center">
                <div className="p-3 bg-primary/10 rounded-full mb-2">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-2xl font-bold leading-tight">Unite & Harmonize</h3>
                  <p className="text-sm text-muted-foreground leading-normal">
                    Collaborate with your partner to lovingly weave your individual answers into a single, unified vision.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 text-center items-center">
                <div className="p-3 bg-primary/10 rounded-full mb-2">
                  <BookOpen className="h-8 w-8 text-primary" />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-2xl font-bold leading-tight">Create Your Contract</h3>
                  <p className="text-sm text-muted-foreground leading-normal">
                    Generate your personalized Love Contract — a memento of your shared commitment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Valentine's Day Promo - temporarily hidden
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <ValentinePromoCard
                variant="default"
                className="border border-border bg-card rounded-xl shadow-none [&>div:first-child]:hidden"
              />
            </div>
          </div>
        </section>
        */}

        {/* Journey Timeline */}
        <section className="py-20 lg:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight text-center mb-12">
              Your Journey Together
            </h2>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-border hidden md:block"></div>

              <div className="grid grid-cols-1 md:grid-cols-[48px_1fr] gap-x-8 gap-y-12">
                {/* Step 1 */}
                <div className="flex md:flex-col items-center gap-4">
                  <div className="flex-shrink-0 size-12 flex items-center justify-center rounded-full bg-primary text-primary-foreground z-10">
                    <Mail className="h-5 w-5" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-bold text-primary tracking-wide uppercase">Step 1</p>
                  <p className="text-xl font-bold mt-1">Invite Your Partner</p>
                  <p className="text-muted-foreground text-base mt-2">
                    Begin your journey by inviting your partner to join you. Each of you will then answer a series of thoughtful questions privately.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="flex md:flex-col items-center gap-4">
                  <div className="flex-shrink-0 size-12 flex items-center justify-center rounded-full bg-primary text-primary-foreground z-10">
                    <Lightbulb className="h-5 w-5" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-bold text-primary tracking-wide uppercase">Step 2</p>
                  <p className="text-xl font-bold mt-1">Review Shared Insights</p>
                  <p className="text-muted-foreground text-base mt-2">
                    Our platform gently highlights areas of alignment and raises awareness for topics that need preventive attention.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="flex md:flex-col items-center gap-4">
                  <div className="flex-shrink-0 size-12 flex items-center justify-center rounded-full bg-primary text-primary-foreground z-10">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-bold text-primary tracking-wide uppercase">Step 3</p>
                  <p className="text-xl font-bold mt-1">Connect and Collaborate</p>
                  <p className="text-muted-foreground text-base mt-2">
                    Come together in a shared space to discuss your insights and craft your unified responses, strengthening your bond.
                  </p>
                </div>

                {/* Step 4 */}
                <div className="flex md:flex-col items-center gap-4">
                  <div className="flex-shrink-0 size-12 flex items-center justify-center rounded-full bg-primary text-primary-foreground z-10">
                    <FileSignature className="h-5 w-5" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-bold text-primary tracking-wide uppercase">Step 4</p>
                  <p className="text-xl font-bold mt-1">Finalize Your Agreement</p>
                  <p className="text-muted-foreground text-base mt-2">
                    With your hearts and minds aligned, generate your Love Contract, a symbol of your journey and commitment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        {/*
        <section className="py-20 lg:py-28 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Love Stories from Our Couples
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="p-8 rounded-xl bg-card border border-border">
                <p className="text-foreground italic">
                  "This process brought us closer than we ever imagined. It turned big conversations into a joyful, creative activity. Our love contract is our most cherished possession."
                </p>
                <div className="flex items-center mt-6">
                  <img
                    className="h-12 w-12 rounded-full object-cover"
                    alt="Alex & Jamie"
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&q=80"
                  />
                  <div className="ml-4">
                    <p className="font-bold">Alex & Jamie</p>
                    <p className="text-sm text-muted-foreground">Engaged Couple</p>
                  </div>
                </div>
              </div>

              <div className="p-8 rounded-xl bg-card border border-border">
                <p className="text-foreground italic">
                  "A way to align on our future together before getting married. It helped us talk about things we hadn't even considered. Highly recommend!"
                </p>
                <div className="flex items-center mt-6">
                  <img
                    className="h-12 w-12 rounded-full object-cover"
                    alt="Sam & Casey"
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&q=80"
                  />
                  <div className="ml-4">
                    <p className="font-bold">Sam & Casey</p>
                    <p className="text-sm text-muted-foreground">Partners for 5 years</p>
                  </div>
                </div>
              </div>

              <div className="p-8 rounded-xl bg-card border border-border">
                <p className="text-foreground italic">
                  "We were looking for something more meaningful than a standard prenup. This was it. It's not about legalities, it's about our promises to each other."
                </p>
                <div className="flex items-center mt-6">
                  <img
                    className="h-12 w-12 rounded-full object-cover"
                    alt="Maria & Ben"
                    src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&q=80"
                  />
                  <div className="ml-4">
                    <p className="font-bold">Maria & Ben</p>
                    <p className="text-sm text-muted-foreground">Newlyweds</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        */}

        {/* CTA Section */}
        <section className="py-20 lg:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to Start Your Next Chapter?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Begin the journey of creating your love contract today. No account needed — start answering questions right away.
            </p>
            <div className="mt-8">
              <Button size="lg" onClick={handleStartFree}>
                Start Free — No Account Needed
              </Button>
            </div>
          </div>
        </section>

        {/* Life-stage question packs */}
        <section className="pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground mb-3">
                Question packs for where you are now
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                What couples argue about changes with the stage they're in. Each pack goes
                deep on one — and folds straight into your agreement.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {allQuestionSets.map((set) => (
                <QuestionSetPromoCard key={set.id} questionSet={set} variant="default" showPrice={false} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Landing;
